import { useMemo } from 'react';

import { EventDateTicket } from '@givebacks/api/store';

const useGetInventoryStatus = (
  eventDateTicketInventories?: EventDateTicket[],
  eventDateCapacity?: number
) => {
  const status = useMemo(() => {
    if (!eventDateTicketInventories) return null;

    const statusSet = eventDateTicketInventories.reduce((statuses, eventDateTicket) => {
      const hasAvailable = (eventDateTicket.inventory?.current_available ?? 0) > 0;
      const isUnlimited = !!eventDateTicket.store_item?.content?.unlimited;
      const isSoldOut = eventDateTicket.inventory?.current_available === 0;

      if (isUnlimited) {
        statuses.add('unlimited');
        // since an unlimited ticket will have 0 available, we don't need to add it to the statuses
      } else if (isSoldOut) statuses.add('soldOut');

      if (hasAvailable) statuses.add('available');

      return statuses;
    }, new Set<string>());

    const ticketCount = eventDateTicketInventories.reduce((count, eventDateTicket) => {
      return count + (eventDateTicket.inventory?.current_available ?? 0);
    }, 0);

    // account for the edgecase where there are no tickets
    if (!statusSet.size) return 'sold out';

    // Handle date capacity first
    if (eventDateCapacity !== undefined && eventDateCapacity !== null) {
      // If date capacity is 0, return sold out
      if (eventDateCapacity === 0) return 'sold out';

      // If there are unlimited tickets, return the date capacity
      if (statusSet.has('unlimited')) {
        return `${eventDateCapacity} left`;
      }

      // If ticket count exceeds date capacity, return date capacity
      if (ticketCount > eventDateCapacity) {
        return `${eventDateCapacity} left`;
      }

      // Otherwise return the ticket count
      return `${ticketCount} left`;
    }

    // Handle cases without date capacity
    if (statusSet.size === 1 && statusSet.has('unlimited')) return null;

    if (statusSet.size === 1 && statusSet.has('soldOut')) return 'sold out';

    if (statusSet.size >= 2) return 'limited';

    return `${ticketCount} left`;
  }, [eventDateTicketInventories, eventDateCapacity]);

  return status;
};
export { useGetInventoryStatus };
